import { defineModel, styleV3, type Part } from "../../lib";

// A boxy WWI motor ambulance: a white canvas box body carrying a poppy-red
// cross on each side, on the rear doors and — the top-down sign — on the
// roof; an open cab with a bench and steering wheel, khaki bonnet, spoked
// wheels. Front is +z.

const { box, cyl } = styleV3;

const KHAKI = "#7a6f4e";
const OLIVE = "#5f6b3c";
const GUNMETAL = "#4a4d52";
const CANVAS = "#cbbd93";
const WHITE = "#e8e4da";
const RED = "#c0392b";
const TYRE = "#33342f";

function wheel(x: number, z: number, r: number): Part[] {
  return [
    cyl(r, r, 0.06, 12, TYRE, [x, r, z], [0, 0, Math.PI / 2]),
    box(0.075, r * 1.65, 0.028, KHAKI, [x, r, z], [Math.PI / 4, 0, 0]),
    box(0.075, r * 1.65, 0.028, KHAKI, [x, r, z], [-Math.PI / 4, 0, 0]),
    cyl(r * 0.28, r * 0.28, 0.09, 8, GUNMETAL, [x, r, z], [0, 0, Math.PI / 2]),
  ];
}

// A red cross made of two bars, oriented by which axis is "flat".
function crossOnSide(x: number, y: number, z: number): Part[] {
  return [
    box(0.02, 0.26, 0.09, RED, [x, y, z]),
    box(0.02, 0.09, 0.26, RED, [x, y, z]),
  ];
}

function parts(): Part[] {
  const R = 0.14;
  const BODY_Y = 0.53;
  const BODY_Z = -0.42;
  const out: Part[] = [
    ...wheel(-0.235, 0.52, R),
    ...wheel(0.235, 0.52, R),
    ...wheel(-0.235, -0.42, R),
    ...wheel(0.235, -0.42, R),
    cyl(0.02, 0.02, 0.47, 6, GUNMETAL, [0, R, 0.52], [0, 0, Math.PI / 2]),
    cyl(0.02, 0.02, 0.47, 6, GUNMETAL, [0, R, -0.42], [0, 0, Math.PI / 2]),
    box(0.34, 0.05, 1.5, GUNMETAL, [0, 0.18, -0.03]),

    // -- Bonnet and radiator -------------------------------------------------
    box(0.3, 0.18, 0.38, KHAKI, [0, 0.34, 0.56]),
    box(0.32, 0.22, 0.04, GUNMETAL, [0, 0.32, 0.76]),
    box(0.08, 0.025, 0.36, OLIVE, [-0.215, 0.31, 0.52]),
    box(0.08, 0.025, 0.36, OLIVE, [0.215, 0.31, 0.52]),

    // -- Open cab -------------------------------------------------------------
    box(0.4, 0.04, 0.34, KHAKI, [0, 0.22, 0.19]),
    box(0.34, 0.1, 0.06, KHAKI, [0, 0.4, 0.34]),
    box(0.38, 0.06, 0.15, OLIVE, [0, 0.3, 0.12]),
    cyl(0.012, 0.012, 0.2, 6, GUNMETAL, [0.11, 0.37, 0.28], [0.9, 0, 0]),
    cyl(0.05, 0.05, 0.018, 10, TYRE, [0.11, 0.445, 0.215], [0.9, 0, 0]),

    // -- Box body with crosses ------------------------------------------------
    box(0.48, 0.42, 0.85, WHITE, [0, BODY_Y, BODY_Z]),
    box(0.52, 0.04, 0.9, CANVAS, [0, 0.76, BODY_Z]), // roof slab
    // side crosses (proud of the 0.24 side faces)
    ...crossOnSide(-0.245, BODY_Y, BODY_Z),
    ...crossOnSide(0.245, BODY_Y, BODY_Z),
    // rear-door cross
    box(0.09, 0.26, 0.02, RED, [0, BODY_Y, -0.85]),
    box(0.26, 0.09, 0.02, RED, [0, BODY_Y, -0.85]),
    // roof cross — the top-down sign
    box(0.12, 0.02, 0.4, RED, [0, 0.785, BODY_Z]),
    box(0.4, 0.02, 0.12, RED, [0, 0.785, BODY_Z]),
  ];
  return out;
}

export const fieldAmbulance = defineModel({
  id: "field-ambulance",
  name: "Field Ambulance",
  batchId: "wwi",
  tags: ["vehicle", "wwi", "military", "ambulance", "medical", "truck"],
  summary: "A boxy white WWI motor ambulance with red crosses on sides and roof.",
  description:
    "A WWI field ambulance: an open cab and khaki bonnet up front, a white " +
    "canvas box body behind, marked with a poppy-red cross on each side, on " +
    "the rear doors and on the roof so it reads as an ambulance from " +
    "top-down.",
  author: "Fable 5",
  footprint: 0.7,
  tiles: { w: 1, h: 2 },
  parts,
  animations: [],
});
